import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import NewPatientModal from "@/components/Patient/NewPatientModal";
import {
  Users, Search, Phone,
  UserPlus, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getPatients } from '@/lib/api';
import { displayAge } from '@/lib/patientAge';

interface Patient {
  id: string;
  name: string;
  age?: number;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  mrn?: string;
  last_visit_date?: string;
  created_at: string;
  updated_at: string;
}

function timeAgo(dateStr?: string): string | null {
  if (!dateStr) return null;
  try {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7)  return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  } catch { return null; }
}

type SortField = 'name' | 'created_at';

export default function PatientManagementPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { document.title = "Patients | OneCheckUp"; }, []);

  const [patients, setPatients]         = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm]     = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [sortField, setSortField]       = useState<SortField>('created_at');
  const [sortOrder, setSortOrder]       = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading]           = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [currentPage, setCurrentPage]   = useState(1);
  const [totalItems, setTotalItems]     = useState(0);
  const itemsPerPage = 20;

  const fetchPatients = useCallback(async () => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    try {
      const result: { patients: Patient[]; total: number } = await getPatients({
        clinic_id: currentUser.clinic_id,
        search: searchTerm,
        gender: genderFilter,
        sortField,
        sortOrder,
        page: currentPage,
        pageSize: itemsPerPage,
      });
      setPatients(result.patients);
      setTotalItems(result.total);
    } catch {
      setPatients([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.clinic_id, searchTerm, genderFilter, sortField, sortOrder, currentPage]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, genderFilter, sortField, sortOrder]);

  // Refresh when a patient is created from anywhere (e.g. GlobalTopBar)
  useEffect(() => {
    const handler = () => fetchPatients();
    window.addEventListener('patient:created', handler);
    return () => window.removeEventListener('patient:created', handler);
  }, [fetchPatients]);

  useEffect(() => {
    if (location.state?.focusSearch) {
      setTimeout(() => {
        const el = document.getElementById('patient-search') as HTMLInputElement;
        el?.focus(); el?.select();
      }, 100);
    }
  }, [location.state]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem  = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem    = Math.min(currentPage * itemsPerPage, totalItems);

  const isFrontDesk = location.pathname.startsWith('/frontdesk');
  const getPatientPath = (id: string) =>
    isFrontDesk ? `/frontdesk/patients/${id}` : `/patients/${id}`;

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const SortBtn = ({ field, label }: { field: SortField; label: string }) => {
    const active = sortField === field;
    const Icon = active ? (sortOrder === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(field)}
        className={cn(
          "flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors",
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        {label} <Icon className="w-3 h-3" />
      </button>
    );
  };

  return (
    <div className="h-full flex flex-col bg-background">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-border space-y-3">

        <div className="flex items-center gap-2">
          <h1 className="text-base font-semibold text-foreground">Patients</h1>
          {totalItems > 0 && !loading && (
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full tabular-nums">{totalItems}</span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
            <Input
              id="patient-search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by name, phone, or MRN…"
              className="pl-8 h-8 text-sm pr-8"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-base leading-none"
              >
                ×
              </button>
            )}
          </div>

          {/* Gender + Sort — unified pill group */}
          <div className="flex items-center gap-0.5 bg-muted/40 rounded-lg p-0.5 flex-shrink-0">
            {(['', 'Male', 'Female'] as const).map(g => (
              <button
                key={g || 'all'}
                type="button"
                onClick={() => setGenderFilter(g)}
                className={cn(
                  "px-2.5 py-1.5 rounded text-xs font-medium transition-colors",
                  genderFilter === g ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {g || 'All'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-0.5 bg-muted/40 rounded-lg p-0.5 flex-shrink-0">
            <SortBtn field="created_at" label="Recent" />
            <SortBtn field="name"       label="Name"   />
          </div>
        </div>
      </div>

      {/* ── List ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-2">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-muted-foreground">Loading…</p>
          </div>

        ) : patients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center px-6">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Users className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {searchTerm ? `No results for "${searchTerm}"` : 'No patients yet'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {searchTerm ? 'Try a different name, phone, or MRN' : 'Register your first patient to get started'}
              </p>
            </div>
            {!searchTerm && (
              <Button size="sm" onClick={() => setShowAddPatient(true)} className="mt-1 gap-1.5">
                <UserPlus className="w-3.5 h-3.5" /> Add First Patient
              </Button>
            )}
          </div>

        ) : (
          <div>
            {patients.map((patient, i) => {
              const initials    = patient.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              const lastVisit   = timeAgo(patient.last_visit_date);
              const demographics = [displayAge(patient.age, patient.date_of_birth), patient.gender].filter(Boolean).join(' · ');

              return (
                <div
                  key={patient.id}
                  onClick={() => navigate(getPatientPath(patient.id))}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors bg-card",
                    "hover:bg-muted/50 active:bg-muted",
                    i < patients.length - 1 && "border-b border-border/40"
                  )}
                >
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 select-none">
                    {initials}
                  </div>

                  {/* Primary info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm font-medium text-foreground truncate">{patient.name}</span>
                      {demographics && (
                        <span className="text-xs text-muted-foreground flex-shrink-0">{demographics}</span>
                      )}
                    </div>
                    {patient.phone && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Phone className="w-2.5 h-2.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">{patient.phone}</span>
                      </div>
                    )}
                  </div>

                  {/* MRN */}
                  {patient.mrn && (
                    <span className="hidden sm:inline-flex text-[11px] font-mono font-semibold text-primary bg-primary/8 px-2 py-0.5 rounded flex-shrink-0">
                      {patient.mrn}
                    </span>
                  )}

                  {/* Last visit — single line */}
                  <div className="hidden md:block flex-shrink-0 w-20 text-right">
                    {lastVisit
                      ? <span className="text-xs text-muted-foreground">{lastVisit}</span>
                      : <span className="text-xs text-muted-foreground/30">—</span>
                    }
                  </div>

                  {/* Chevron */}
                  <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-t border-border bg-background">
          <span className="text-xs text-muted-foreground tabular-nums">
            {startItem}–{endItem} of {totalItems}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs px-2 tabular-nums text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showAddPatient && (
        <NewPatientModal
          isOpen={showAddPatient}
          onClose={() => setShowAddPatient(false)}
          onPatientCreated={() => { fetchPatients(); setShowAddPatient(false); }}
        />
      )}
    </div>
  );
}
